export type ActionCardMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ActionCardAction {
  method: ActionCardMethod;
  endpoint: string;
  payloadPaths?: Record<string, string>;
  payload?: Record<string, unknown>;
}

export interface ActionCardBase {
  type: string;
  title?: string;
  button: string;
}

export interface ActionCardSendEmail extends ActionCardBase {
  type: 'send-email';
  recipients: Array<{ name: string; email: string; id?: string }>;
  subject?: string;
  content: string;
}

export interface ActionCardCopyableText extends ActionCardBase {
  type: 'copyable-text';
  content: string;
}

export interface ActionCardSendData extends ActionCardBase {
  type: 'send-data';
  fields?: Array<{ key: string; label: string; value?: string }>;
  content?: string;
}

export type ActionCardData =
  | ActionCardSendEmail
  | ActionCardCopyableText
  | ActionCardSendData;

export interface ActionCardPayload {
  version?: string;
  createdTs: string;
  action: ActionCardAction;
  card: ActionCardData;
}

export function isActionCardSendEmail(
  payload: ActionCardPayload
): payload is ActionCardPayload & { card: ActionCardSendEmail } {
  return payload?.card?.type === 'send-email';
}

export function isActionCardPayload(
  value: unknown
): value is ActionCardPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    'action' in value &&
    'card' in value &&
    typeof (value as ActionCardPayload).card?.type === 'string'
  );
}

export interface SendEmailActionCardAction {
  method?: string;
  protocol?: string;
  params?: Record<string, string>;
  cts?: number;
  endpoint?: string;
  url?: string;
}

export interface SendEmailActionCardCard {
  title?: string;
  content: string;
  button: string;
  subject?: string;
  recipients?: Array<{ name?: string; email: string; id?: string }>;
  receipients?: Array<{ name?: string; email: string; id?: string }>;
}

export interface SendEmailActionCardPayload {
  type: 'send-email';
  editable?: boolean;
  action: SendEmailActionCardAction;
  card: SendEmailActionCardCard;
}

export function isSendEmailActionCardPayload(
  value: unknown
): value is SendEmailActionCardPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const rawType =
    typeof v['type'] === 'string'
      ? v['type'].toLowerCase().replace(/[-_ ]/g, '')
      : '';
  return rawType === 'sendemail' && 'action' in v && 'card' in v;
}

export interface ExcelDownloadActionCardCard {
  title?: string;
  filename: string;
  sizeBytes: number;
  sheetCount: number;
  button: string;
  docId: string;
}

export interface ExcelDownloadActionCardPayload {
  type: 'download-excel';
  editable?: boolean;
  action: SendEmailActionCardAction;
  card: ExcelDownloadActionCardCard;
}

export function isExcelDownloadActionCardPayload(
  value: unknown
): value is ExcelDownloadActionCardPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as ExcelDownloadActionCardPayload).type === 'download-excel' &&
    'action' in value &&
    'card' in value
  );
}

export interface WordDownloadActionCardCard {
  title?: string;
  filename: string;
  sizeBytes: number;
  sectionCount?: number;
  button: string;
  docId: string;
}

export interface WordDownloadActionCardPayload {
  type: 'download-word';
  editable?: boolean;
  action: SendEmailActionCardAction;
  card: WordDownloadActionCardCard;
}

export function isWordDownloadActionCardPayload(
  value: unknown
): value is WordDownloadActionCardPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as WordDownloadActionCardPayload).type === 'download-word' &&
    'action' in value &&
    'card' in value
  );
}

export interface CreateNoteActionCardCard {
  title?: string;
  content: string;
  button: string;
}

export interface CreateNoteActionCardPayload {
  type: 'create-note';
  editable?: boolean;
  action: SendEmailActionCardAction;
  card: CreateNoteActionCardCard;
}

export function isCreateNoteActionCardPayload(
  value: unknown
): value is CreateNoteActionCardPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const rawType =
    typeof v['type'] === 'string'
      ? v['type'].toLowerCase().replace(/[-_ ]/g, '')
      : '';
  return rawType === 'createnote' && 'action' in v && 'card' in v;
}

export interface CreateTaskActionCardCard {
  title: string;
  description?: string;
  dueDate?: string;
  assignee?: string;
  button: string;
}

export interface CreateTaskActionCardPayload {
  type: 'create-task';
  editable?: boolean;
  action: SendEmailActionCardAction;
  card: CreateTaskActionCardCard;
}

export function isCreateTaskActionCardPayload(
  value: unknown
): value is CreateTaskActionCardPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const rawType =
    typeof v['type'] === 'string'
      ? v['type'].toLowerCase().replace(/[-_ ]/g, '')
      : '';
  return rawType === 'createtask' && 'action' in v && 'card' in v;
}

// ─── SD-2326: dynamic action-card (Langgraph proposeAction) ─────────────────
// Replaces the hand-coded CreateNote/CreateTask cards above. The BE produces
// one of these whenever the agent pauses at a `proposeAction` node (note,
// task, eventually deal, etc.). The card is editable; on Accept the FE POSTs
// the (possibly edited) values + `pendingApprovalId` to
// `/api/v1/allo/approve-action` and the BE resumes the graph.

export type DynamicActionCardFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select';

export interface DynamicActionCardFieldOption {
  value: string;
  label: string;
}

export interface DynamicActionCardEditableField {
  key: string;
  label: string;
  type: DynamicActionCardFieldType;
  options?: DynamicActionCardFieldOption[];
}

export interface DynamicActionCardCard {
  button: string;
  title?: string;
  description?: string;
  entityType: string;
  pendingApprovalId: string;
  editableFields: DynamicActionCardEditableField[];
  values: Record<string, unknown>;
}

export interface DynamicActionCardPayload {
  type: string;
  editable?: boolean;
  action: SendEmailActionCardAction;
  card: DynamicActionCardCard;
}

export function isDynamicActionCardPayload(
  value: unknown
): value is DynamicActionCardPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const card = v['card'] as Record<string, unknown> | undefined;
  return (
    typeof card === 'object' &&
    card !== null &&
    Array.isArray(card['editableFields']) &&
    typeof card['pendingApprovalId'] === 'string'
  );
}
